/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { TypographyComponent } from '../typography/typography.component';

export interface SearchEvent {
  searchTerm: string;
  timestamp: number;
}

@Component({
  selector: 'hyperkit-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconComponent,
    ButtonComponent,
    TypographyComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
})
export class SearchInputComponent implements ControlValueAccessor {
  @Input() text = '';
  @Input() placeholder = 'Search...';
  @Input() disabled = false;

  @Output() search = new EventEmitter<SearchEvent>();
  @Output() textChange = new EventEmitter<string>();

  // These functions allow Angular forms to interact with this component
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor: Called by Angular when it needs to write the value to the input
  writeValue(value: string): void {
    if (value !== undefined) {
      this.text = value;
    }
  }

  // ControlValueAccessor: Registers the onChange function
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  // ControlValueAccessor: Registers the onTouched function
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Optional: This will be called when the form control is disabled
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Local text change handler
  onTextChange(value: string) {
    this.text = value;
    this.textChange.emit(value);
    this.onChange(value); // Notify Angular form of the change
  }

  // Triggered when the search button is clicked
  public onSearch(): void {
    if (!this.disabled) {
      const searchEvent: SearchEvent = {
        searchTerm: this.text,
        timestamp: Date.now(),
      };
      this.search.emit(searchEvent);
    }
  }

  // Triggered when the cancel button is clicked
  public onCancel(): void {
    if (!this.disabled) {
      this.text = '';
      this.textChange.emit(''); // ✅ emit the cleared text!
      this.onChange('');        // ✅ notify Angular forms if using
      this.onSearch();
    }
  }
  
  // Trigger onBlur to notify that the field has been touched
  onBlur(): void {
    this.onTouched();
  }
}
